import React, { useEffect, useState } from 'react';
import type { OperationsTvHealth, OperationsTvSummary } from './types';

const statTone: Record<'total' | 'flight' | 'ground' | 'alert', string> = {
  total: 'text-slate-100',
  flight: 'text-emerald-300',
  ground: 'text-sky-200',
  alert: 'text-amber-300',
};

const healthToneClass: Record<OperationsTvHealth['tone'], string> = {
  healthy: 'border-emerald-300/40 bg-emerald-500/10 text-emerald-200',
  warning: 'border-amber-300/40 bg-amber-500/10 text-amber-200',
  danger: 'border-rose-300/40 bg-rose-500/10 text-rose-200',
};

const OperationsTvHeader: React.FC<{
  summary: OperationsTvSummary;
  health: OperationsTvHealth;
  now: Date;
  tvMode: boolean;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  lastRefresh: string;
}> = ({ summary, health, now, tvMode, isFullscreen, onToggleFullscreen, lastRefresh }) => {
  const [kpiRefreshFx, setKpiRefreshFx] = useState(false);

  useEffect(() => {
    if (!lastRefresh) return undefined;
    setKpiRefreshFx(true);
    const timer = window.setTimeout(() => setKpiRefreshFx(false), 950);
    return () => window.clearTimeout(timer);
  }, [lastRefresh]);

  const dateText = now.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
  const timeText = now.toLocaleTimeString('en-GB', { hour12: false });

  return (
    <header
      className={`relative overflow-hidden rounded-3xl border border-amber-300/30 bg-[linear-gradient(122deg,rgba(5,12,28,0.96)_0%,rgba(8,20,46,0.9)_42%,rgba(9,14,30,0.96)_100%)] shadow-[0_30px_100px_rgba(2,6,23,0.78)] backdrop-blur-xl ${
        tvMode ? 'p-7 lg:p-8' : 'p-6'
      } ops-tv-glass-panel`}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,rgba(245,158,11,0.16),transparent_38%),radial-gradient(circle_at_85%_15%,rgba(16,185,129,0.08),transparent_34%)]" />

      <div className="relative flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-[280px]">
          <div className="flex items-center gap-2">
            <span className="ops-tv-live-node inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_14px_rgba(52,211,153,0.85)]" />
            <p className="text-[10px] font-black uppercase tracking-[0.28em] text-amber-200/90">Miracle Air Command Center</p>
            <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-400">System Link Stable</p>
          </div>
          <h1 className={`mt-2 font-black tracking-tight text-white ${tvMode ? 'text-5xl' : 'text-3xl'}`}>Operations TV Board</h1>
          <p className={`mt-1 font-semibold text-slate-300 ${tvMode ? 'text-sm' : 'text-xs'}`}>Executive live fleet wallboard • premium monitor mode</p>
        </div>

        <div className="flex items-center gap-4">
          <div className={`rounded-2xl border border-amber-300/35 bg-[linear-gradient(145deg,rgba(9,16,34,0.92),rgba(3,10,24,0.9))] text-right shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_12px_36px_rgba(2,6,23,0.6)] ${tvMode ? 'px-6 py-4' : 'px-5 py-3'}`}>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">System Time · EAT</p>
            <p className={`ops-tv-clock mt-1 font-mono font-black text-amber-200 drop-shadow-[0_0_14px_rgba(251,191,36,0.45)] ${tvMode ? 'text-5xl' : 'text-3xl'}`}>{timeText}</p>
            <p className="mt-1 text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">{dateText}</p>
          </div>
          <button
            type="button"
            onClick={onToggleFullscreen}
            className={`rounded-xl border border-amber-300/45 bg-[linear-gradient(135deg,rgba(180,83,9,0.4),rgba(245,158,11,0.22))] font-black uppercase tracking-[0.18em] text-amber-100 shadow-[0_10px_24px_rgba(245,158,11,0.25)] transition hover:brightness-110 hover:shadow-[0_14px_32px_rgba(245,158,11,0.4)] ${tvMode ? 'px-5 py-2.5 text-[11px]' : 'px-4 py-2 text-[10px]'}`}
          >
            {isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          </button>
        </div>
      </div>

      <div className={`relative mt-6 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-5 ${tvMode ? '2xl:gap-4' : ''}`}>
        <div className={`ops-tv-kpi-card rounded-2xl border border-white/10 bg-[linear-gradient(145deg,rgba(17,24,39,0.86),rgba(10,16,30,0.92))] px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] ${kpiRefreshFx ? 'ops-tv-kpi-refresh' : ''}`}>
          <p className="text-[9px] font-black uppercase tracking-[0.18em] text-slate-400">Total Fleet</p>
          <p className={`mt-1 text-3xl font-black ${statTone.total}`}>{summary.total}</p>
        </div>
        <div className={`ops-tv-kpi-card rounded-2xl border border-emerald-300/35 bg-[linear-gradient(145deg,rgba(16,185,129,0.18),rgba(3,19,17,0.85))] px-4 py-3 shadow-[0_0_30px_rgba(16,185,129,0.18)] ${kpiRefreshFx ? 'ops-tv-kpi-refresh' : ''}`}>
          <p className="text-[9px] font-black uppercase tracking-[0.18em] text-emerald-200/85">In Flight</p>
          <p className={`mt-1 text-3xl font-black drop-shadow-[0_0_12px_rgba(16,185,129,0.45)] ${statTone.flight}`}>{summary.inFlight}</p>
        </div>
        <div className={`ops-tv-kpi-card rounded-2xl border border-sky-300/35 bg-[linear-gradient(145deg,rgba(14,165,233,0.15),rgba(4,15,32,0.86))] px-4 py-3 shadow-[0_0_30px_rgba(14,165,233,0.15)] ${kpiRefreshFx ? 'ops-tv-kpi-refresh' : ''}`}>
          <p className="text-[9px] font-black uppercase tracking-[0.18em] text-sky-200/85">On Ground</p>
          <p className={`mt-1 text-3xl font-black ${statTone.ground}`}>{summary.onGround}</p>
        </div>
        <div className={`ops-tv-kpi-card rounded-2xl border border-amber-300/35 bg-[linear-gradient(145deg,rgba(245,158,11,0.16),rgba(35,18,6,0.88))] px-4 py-3 shadow-[0_0_30px_rgba(245,158,11,0.18)] ${kpiRefreshFx ? 'ops-tv-kpi-refresh' : ''}`}>
          <p className="text-[9px] font-black uppercase tracking-[0.18em] text-amber-200/85">Tracking</p>
          <p className={`mt-1 text-3xl font-black ${statTone.alert}`}>{summary.alerts}</p>
        </div>
        <div className={`ops-tv-kpi-card rounded-2xl border px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] ${healthToneClass[health.tone]} ${kpiRefreshFx ? 'ops-tv-kpi-refresh' : ''}`}>
          <p className="text-[9px] font-black uppercase tracking-[0.18em]">Tracker Health</p>
          <p className="mt-1 text-[13px] font-black uppercase tracking-[0.08em] ops-tv-health-shimmer">{health.label}</p>
        </div>
      </div>
    </header>
  );
};

export default OperationsTvHeader;
